import type { ReactNode } from 'react';
import { T } from 'gt-react';
import { DiscountElementTargetType } from '@erp/shared-types';
import type { DiscountElementV2 } from '@erp/rpc';
import {
  discountAmountLabel,
  effectiveWindowLabel,
  formatQuantity,
  usageLabel,
} from './discountFormat';
import { DiscountTypePill, TargetTypePill } from './discountPills';

function elementStateLabel(element: DiscountElementV2): ReactNode {
  if (element.isConsumed)
    return <T context="discount element state">Consumed</T>;
  if (element.isActive) return <T context="discount element state">Active</T>;
  return <T context="discount element state">Inactive</T>;
}

/** The element's target — a resolved SKU label for variant targets, else the raw segment. */
export function elementTargetLabel(element: DiscountElementV2): string {
  return (
    element.targetLabel ??
    element.targetValue ??
    (element.targetType === DiscountElementTargetType.ALL_PRODUCTS
      ? 'All products'
      : '—')
  );
}

/** The element's target next to its target-type pill (side-pane / focus headers). */
export function ElementTargetHeading({
  element,
}: {
  element: DiscountElementV2;
}) {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className="truncate font-medium">
        {elementTargetLabel(element)}
      </span>
      <TargetTypePill targetType={element.targetType} />
    </span>
  );
}

function Field({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="space-y-0.5">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

/**
 * Read-only field grid for a single discount element. Usage lives in its own
 * tab / section alongside this, so it is intentionally not embedded here.
 */
export function ElementDetailFields({
  element,
}: {
  element: DiscountElementV2;
}) {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
      <Field label={<T context="element detail field">Target</T>}>
        <ElementTargetHeading element={element} />
      </Field>
      <Field label={<T context="element detail field">Discount type</T>}>
        <DiscountTypePill discountType={element.discountType} />
      </Field>
      <Field label={<T context="element detail field">Value</T>}>
        {discountAmountLabel(element.discountType, element.discountValue)}
      </Field>
      <Field label={<T context="element detail field">Effective window</T>}>
        {effectiveWindowLabel(element.effectiveStart, element.effectiveEnd)}
      </Field>
      <Field label={<T context="element detail field">Usage</T>}>
        {usageLabel(element.usedQuantity, element.maxQuantity)}
      </Field>
      <Field label={<T context="element detail field">Max quantity</T>}>
        {element.maxQuantity != null ? (
          formatQuantity(element.maxQuantity)
        ) : (
          <T context="element detail unlimited max quantity">Unlimited</T>
        )}
      </Field>
      <Field label={<T context="element detail field">Single-use</T>}>
        {element.isSingleUse ? <T>Yes</T> : <T>No</T>}
      </Field>
      <Field label={<T context="element detail field">Status</T>}>
        {elementStateLabel(element)}
      </Field>
    </div>
  );
}
